/**
 * LC 앱 i18n 초기화 (ko/en)
 *
 * Company 앱과 동일한 패턴으로 다국어(ko/en)를 지원합니다.
 * shared-learning의 퀴즈 번역 리소스도 함께 로드합니다.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { sharedLearningResources } from '@quebon/shared-learning'

import koMarketing from './locales/ko/marketing.json'
import enMarketing from './locales/en/marketing.json'
import { readMarketingLanguage } from './marketing'
import { isLandingPage } from '@/utils/tenant'

// Korean
import koCommon from './locales/ko/common.json'
import koAuth from './locales/ko/auth.json'
import koNav from './locales/ko/nav.json'
import koDashboard from './locales/ko/dashboard.json'
import koLearner from './locales/ko/learner.json'
import koAdmin from './locales/ko/admin.json'
import koCoaching from './locales/ko/coaching.json'
import koCoachingAltPage from './locales/ko/coaching-alt-page.json'
import koCoachingAltContent from './locales/ko/coaching-alt-content.json'
import koCoachingAltLesson from './locales/ko/coaching-alt-lesson.json'
import koCoachingAltQueue from './locales/ko/coaching-alt-queue.json'
import koTodayDirectory from './locales/ko/coaching-today-directory.json'
import koTodayWorkspace from './locales/ko/coaching-today-workspace.json'
import koTodayGrading from './locales/ko/coaching-today-grading.json'
import koTodayContent from './locales/ko/coaching-today-content.json'
import koAnnouncements from './locales/ko/announcements.json'
import koMall from './locales/ko/mall.json'
import koVideo from './locales/ko/video.json'

// English
import enCommon from './locales/en/common.json'
import enAuth from './locales/en/auth.json'
import enNav from './locales/en/nav.json'
import enDashboard from './locales/en/dashboard.json'
import enLearner from './locales/en/learner.json'
import enAdmin from './locales/en/admin.json'
import enCoaching from './locales/en/coaching.json'
import enCoachingAltPage from './locales/en/coaching-alt-page.json'
import enCoachingAltContent from './locales/en/coaching-alt-content.json'
import enCoachingAltLesson from './locales/en/coaching-alt-lesson.json'
import enCoachingAltQueue from './locales/en/coaching-alt-queue.json'
import enTodayDirectory from './locales/en/coaching-today-directory.json'
import enTodayWorkspace from './locales/en/coaching-today-workspace.json'
import enTodayGrading from './locales/en/coaching-today-grading.json'
import enTodayContent from './locales/en/coaching-today-content.json'
import enAnnouncements from './locales/en/announcements.json'
import enMall from './locales/en/mall.json'
import enVideo from './locales/en/video.json'

export const SUPPORTED_LANGUAGES = ['ko', 'en'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ko: {
        common: koCommon,
        marketing: koMarketing,
        auth: koAuth,
        nav: koNav,
        dashboard: koDashboard,
        learner: koLearner,
        admin: koAdmin,
        coaching: { ...koCoaching, ...koCoachingAltPage, ...koCoachingAltContent, ...koCoachingAltLesson, ...koCoachingAltQueue, ...koTodayDirectory, ...koTodayWorkspace, ...koTodayGrading, ...koTodayContent },
        announcements: koAnnouncements,
        mall: koMall,
        video: koVideo,
        ...sharedLearningResources.ko,
      },
      en: {
        common: enCommon,
        marketing: enMarketing,
        auth: enAuth,
        nav: enNav,
        dashboard: enDashboard,
        learner: enLearner,
        admin: enAdmin,
        coaching: { ...enCoaching, ...enCoachingAltPage, ...enCoachingAltContent, ...enCoachingAltLesson, ...enCoachingAltQueue, ...enTodayDirectory, ...enTodayWorkspace, ...enTodayGrading, ...enTodayContent },
        announcements: enAnnouncements,
        mall: enMall,
        video: enVideo,
        ...sharedLearningResources.en,
      },
    },
    lng: isLandingPage() ? readMarketingLanguage() : undefined,
    defaultNS: 'common',
    fallbackLng: 'ko',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: isLandingPage() ? [] : ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  })

export default i18n
